import { api } from './client'
import type { ScanLogsResponse } from '../pages/security/components/ScanProgress/types'

export interface SecurityScan {
  id: number
  tenant_id: number
  scan_type: string
  status: string
  overall_score: number | null
  scores_by_category: Record<string, number>
  findings_summary: Record<string, number>
  started_at: string
  completed_at: string | null
}

export interface SecurityFinding {
  id: number
  scan_id: number
  tenant_id: number
  category: string
  check_id: string
  title: string
  severity: string
  status: string
  resource_type: string
  resource_arn: string
  region: string
  details: any
  remediation: string
  first_seen_at: string
  resolved_at: string | null
}

/** Um item do heatmap OWASP: pior severidade aberta + se foi avaliada no último scan. */
export interface CategorySummary {
  id: string
  open_count: number
  worst_severity: string | null
  evaluated: boolean
}

export interface SecurityOverview {
  latest_scans: SecurityScan[]
  findings_by_severity: Record<string, number>
  category_counts: Record<string, number>
  categories: CategorySummary[]
  critical_findings: SecurityFinding[]
  /** Findings abertos (resolved_at IS NULL) — não inflam com re-scan. */
  open_count: number
  critical_count: number
  overall_score: number
  last_scan: { id: number; status: string; completed_at: string | null } | null
}

/** Overview da aba Pentest: os três baldes restritos à lente PENTEST_CHECK_IDS. */
export interface PentestOverview {
  tenant_id: number
  check_ids: string[]
  em_aberto: Record<string, number>
  em_aberto_total: number
  excecao_aceita: number
  corrigido: number
  ultima_varredura: { id: number; status: string; completed_at: string | null } | null
}

/** Um ponto da série temporal da postura (um por scan concluído). */
export interface TrendPoint {
  scan_id: number
  started_at: string | null
  completed_at: string | null
  findings_by_severity: Record<string, number>
  scores_by_category: Record<string, number>
  total: number
}

export interface SecurityTrend {
  tenant_id: number
  points: TrendPoint[]
}

export const securityApi = {
  triggerScan: (tenantId: number, scanType = 'full') =>
    api.post(`/v1/tenants/${tenantId}/security/scans`, { scan_type: scanType }).then(r => r.data),

  listScans: (tenantId: number) =>
    api.get<SecurityScan[]>(`/v1/tenants/${tenantId}/security/scans`).then(r => r.data),

  getScanResults: (tenantId: number, scanId: number) =>
    api.get<SecurityScan>(`/v1/tenants/${tenantId}/security/scans/${scanId}`).then(r => r.data),

  listFindings: (tenantId: number, params?: { severity?: string; category?: string; aberto?: boolean; limit?: number; offset?: number }) =>
    api.get<{ total: number; findings: SecurityFinding[] }>(`/v1/tenants/${tenantId}/security/findings`, { params }).then(r => r.data),

  getOverview: (tenantId: number) =>
    api.get<SecurityOverview>(`/v1/tenants/${tenantId}/security/overview`).then(r => r.data),

  getTrend: (tenantId: number) =>
    api.get<SecurityTrend>(`/v1/tenants/${tenantId}/security/trend`).then(r => r.data),

  getPentestOverview: (tenantId: number) =>
    api.get<PentestOverview>(`/v1/tenants/${tenantId}/security/pentest/overview`).then(r => r.data),

  updateFinding: (tenantId: number, findingId: number, action: string) =>
    api.patch(`/v1/tenants/${tenantId}/security/findings/${findingId}`, { action }).then(r => r.data),

  getScanLogs: (tenantId: number, scanId: number) =>
    api.get<ScanLogsResponse>(`/v1/tenants/${tenantId}/security/scans/${scanId}/logs`).then(r => r.data),

  getCrosstenantOverview: () =>
    api.get<SecurityOverview>('/v1/security/overview').then(r => r.data),
}
